import api from './api';

export interface DocAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at?: string | null;
}

export interface DocAssistantConversationSummary {
  id: number;
  title?: string | null;
  updated_at?: string | null;
  running?: boolean;
}

export interface DocAssistantConversationDetail {
  id: number;
  title?: string | null;
  messages: DocAssistantMessage[];
  running_job_id?: number | null;
}

export interface StartJobResponse {
  job_id: number;
  status: string;
}

export interface JobStatus {
  id: number;
  status: 'running' | 'done' | 'error';
  message?: DocAssistantMessage | null;
  error?: string | null;
}

export interface DocAssistantDocument {
  id: number;
  name: string;
  kind: 'input' | 'output';
  file_type?: string | null;
  created_at?: string | null;
}

export const docAssistantService = {
  async listConversations(): Promise<DocAssistantConversationSummary[]> {
    const response = await api.get('/doc-assistant/conversations');
    return response.data;
  },

  async createConversation(title?: string): Promise<DocAssistantConversationDetail> {
    const response = await api.post('/doc-assistant/conversations', { title: title || null });
    return response.data;
  },

  async getConversation(id: number): Promise<DocAssistantConversationDetail> {
    const response = await api.get(`/doc-assistant/conversations/${id}`);
    return response.data;
  },

  async sendMessage(id: number, content: string): Promise<StartJobResponse> {
    const response = await api.post(`/doc-assistant/conversations/${id}/messages`, { content });
    return response.data;
  },

  async getJob(jobId: number): Promise<JobStatus> {
    const response = await api.get(`/doc-assistant/jobs/${jobId}`);
    return response.data;
  },

  async deleteConversation(id: number): Promise<void> {
    await api.delete(`/doc-assistant/conversations/${id}`);
  },

  // ── documents ──────────────────────────────────────────────────────────────

  async listDocuments(): Promise<DocAssistantDocument[]> {
    const response = await api.get('/doc-assistant/documents');
    return response.data;
  },

  async uploadDocument(name: string, file: globalThis.File): Promise<DocAssistantDocument> {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('file', file);
    const response = await api.post('/doc-assistant/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteDocument(id: number): Promise<void> {
    await api.delete(`/doc-assistant/documents/${id}`);
  },

  async downloadDocument(doc: DocAssistantDocument, format: 'docx' | 'pdf' = 'docx'): Promise<void> {
    const response = await api.get(`/doc-assistant/documents/${doc.id}/download`, {
      params: { format },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.name}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
